import { FC, useEffect, useMemo, useState } from "react"
import {
  Box,
  Dialog,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { UButton, UText } from "@/components/index"
import { ExpandMore } from "@mui/icons-material"
import UTableSearch from "../PatientList/components/UTable/UTableSearch"
import PersonIcon from "@mui/icons-material/Person"
import CachedIcon from "@mui/icons-material/Cached"

import { useAppDispatch, useAppSelector, useDeepEffect } from "@/core/app/hooks"
import { RootState } from "@/core/app/store"
import {
  get3ShapeCaseList,
  getBirthdayfor3Shape,
} from "@/core/app/slices/patients/patientThunkApi"
import useLocalStorageListener from "@/modules/Settings/Integrations/localStorageHooks"
import {
  setData,
  setMeditLoginPopUp,
} from "@/core/app/slices/patients/patientSlice"
import { usePageHeight } from "@/UDTreat/top/GetViewWidth"
import { debounce } from "lodash"

import { getCaseDetail } from "@/core/global/ThirdDataMgr"
import { getCaseByUuid } from "@/core/global/httpMedit"
import { getPatientslist } from "@/core/global/httpAlliedstar"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogContentText from "@mui/material/DialogContentText"
import { resetPatientListDetails, setSelectedScannerPatient } from "@/core/app/slices/integrations"
import { SCANNER_INTEGRATION_IDS, type ScannerIntegrationId } from "@/core/app/slices/integrations/scannerConstants"
import {
  EASY_RX_DISPLAY_NAME,
  getIntegrationScannerConfig,
  isIntegrationScanner,
} from "./scannerIntegrations.config"
import { IntegrationNotConnectedDialog } from "./IntegrationNotConnectedDialog"
import { useIntegrationPatientList } from "./useIntegrationPatientList"
import { useIntegrationScannerOptions } from "./useIntegrationScannerOptions"
const { VITE_DOMAIN } = import.meta.env

// Need client confirmation to enable 
const HIGHLIGHT_CURRENT_PRESCRIPTION = false

const isDeviceScanner = (type: string) =>
  type === "3Shape" || type === "Medit" || type === "Alliedstar"

type ScannerOption = {
  name: string
  key: string
  isPrimary?: boolean
  localName?: string
  backendIntegration?: boolean
}

const ScannerPatients: FC<{
  showScannerPatients: boolean
  setShowScannerPatients: (show: boolean) => void
  choosePatient?: (patient: any) => void
  handleScannerPatientDetailSelect?: ((patient: any) => void) | null
}> = ({
  showScannerPatients,
  setShowScannerPatients,
  choosePatient,
  handleScannerPatientDetailSelect = null,
}) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const {
    caseListFor3Shape,
    scannerSelect,
    scannerCaseListCount,
    meditLoginPopUp,
  } = useAppSelector((state: RootState) => state.patientService)

  const { connectionStatus } = useAppSelector(
    (state: RootState) => state.integrationsService,
  )
  const prescriptionId = useAppSelector(
    (state: RootState) => state.caseService.caseDetail?.extra_info?.easyrx?.prescription_id,
  )
  const connectedIntegrations = useMemo(() => {
    return {
      [SCANNER_INTEGRATION_IDS.EASYRX]: connectionStatus[SCANNER_INTEGRATION_IDS.EASYRX] === true,
    }
  }, [connectionStatus])
  const [filterList, setFilterList] = useState<any[]>([])
  const [searchText, setSearchText] = useState<string>("")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  // One debounced fn for component lifetime; inline debounce() would reset debounce each render!
  const debouncedSetSearchText = useMemo(
    () => debounce((value: string) => setSearchText(value), 1000),
    [],
  )
  useEffect(
    () => () => {
      debouncedSetSearchText.cancel()
    },
    [debouncedSetSearchText],
  )

  const formatUTC = (dateStr) => {
    if (!dateStr) {
      return ""
    }
    const date = new Date(dateStr)

    // 提取UTC时间分量
    const month = date.getUTCMonth() + 1 // 0-11 → 1-12
    const day = date.getUTCDate()
    const year = date.getUTCFullYear()
    let hours = date.getUTCHours()
    const minutes = String(date.getUTCMinutes()).padStart(2, "0")

    // 转换为12小时制AM/PM
    const period = hours >= 12 ? "PM" : "AM"
    hours = hours % 12 || 12 // 0点转为12 AM

    return `${month}/${day}/${year}, ${hours}:${minutes} ${period}`
  }

  const [scannerSelectData, setScannerSelectData] = useState<ScannerOption[]>(
    [],
  )

  // Integration scanners: connection refresh, suppress, not-connected modal, Select onChange checks.
  const {
    suppressScanner,
    notConnectedDialogScanner,
    setNotConnectedDialogScanner,
    closeNotConnectedDialog,
    isIntegrationListAllowed,
    shouldIncludeIntegration,
    checkIntegrationConnection,
  } = useIntegrationScannerOptions({
    showScannerPatients,
    scannerSelect,
    connectedIntegrations,
    scannerSelectData,
    setShowScannerPatients,
  })

  /** Resolved from dropdown `scannerSelect` — only set for registry-backed integrations. */
  const selectedIntegrationConfig = useMemo(
    () => getIntegrationScannerConfig(scannerSelect) ?? null,
    [scannerSelect],
  )

  const integrationListAllowed = isIntegrationListAllowed(scannerSelect)

  // locastorage key  3shapeData， meditData, alliesData
  const shapeLocal = useLocalStorageListener("3shapeData", null)
  const meditDataLocal = useLocalStorageListener("meditData", null)
  const alliesDataLocal = useLocalStorageListener("alliesData", null)

  // get scanner list params
  const [listParams, setListParams] = useState<{
    page: number
    type: string
    name: string
    location: string | null
  }>({ page: 0, type: "", name: "", location: null })

  // Backend patient list: `scannerConfig` follows selected integration (EasyRx when that row is selected).
  const {
    syncNow,
    lastSyncedRelative,
    patientListDetails,
    syncNowClickHandler,
    clearIntegrationTableRows,
  } = useIntegrationPatientList({
    scannerConfig: selectedIntegrationConfig,
    showScannerPatients,
    integrationListAllowed,
    listParams,
    searchText,
    rowsPerPage,
    scannerSelect,
    setFilterList,
  })

  const cleanup = () => {
    setPage(0)
    setListParams({ page: 0, type: listParams.type, name: "", location: null })
    setFilterList([])
    setSearchText("")
    dispatch(resetPatientListDetails())
  }

  useEffect(() => {
    return cleanup
  }, [])

  useEffect(() => {
    if (!showScannerPatients) {
      cleanup()
    }
  }, [showScannerPatients, dispatch])

  // Device scanners (localStorage) + integration scanners (registry / backend connection)
  useDeepEffect(() => {
    const tmp: ScannerOption[] = []
    if (shapeLocal) {
      const shapeScanner = JSON.parse(
        localStorage.getItem("3shapeData") || "{}",
      )
      tmp.push({
        name: "3Shape",
        key: "3Shape",
        isPrimary: shapeScanner.isPrimary,
        localName: "3shapeData",
      })
    }
    if (meditDataLocal) {
      const meditScanner = JSON.parse(localStorage.getItem("meditData") || "{}")
      tmp.push({
        name: "Medit",
        key: "Medit",
        isPrimary: meditScanner.isPrimary,
        localName: "meditData",
      })
    }
    if (alliesDataLocal) {
      const allieScanner = JSON.parse(
        localStorage.getItem("alliesData") || "{}",
      )
      tmp.push({
        name: "Alliedstar",
        key: "Alliedstar",
        isPrimary: allieScanner.isPrimary,
        localName: "alliesData",
      })
    }
    const easyRxConfig = getIntegrationScannerConfig(SCANNER_INTEGRATION_IDS.EASYRX)
    if (easyRxConfig && shouldIncludeIntegration(easyRxConfig)) {
      tmp.push({
        name: EASY_RX_DISPLAY_NAME,
        key: easyRxConfig.key,
        isPrimary: false,
        backendIntegration: true,
      })
    }
    setScannerSelectData(tmp)
  }, [
    shapeLocal,
    meditDataLocal,
    alliesDataLocal,
    shouldIncludeIntegration,
  ])

  // set primary select when options change or dialog opens
  useDeepEffect(() => {
    if (!scannerSelectData.length) {
      return
    }
    let primaryScanner = ""
    scannerSelectData.forEach((i) => {
      if (i.isPrimary) {
        primaryScanner = i.key
      }
    })
    if (!primaryScanner) {
      primaryScanner = scannerSelectData[0].key
    }
    dispatch(setData({ scannerSelect: primaryScanner }))
    setListParams({ page: 0, type: primaryScanner, name: "", location: null })
  }, [scannerSelectData, showScannerPatients])

  useDeepEffect(() => {
    setPage(0)
    setListParams({ page: 0, name: searchText, type: listParams.type, location: listParams.location })
  }, [searchText])

  useDeepEffect(() => {
    setListParams({ page: page, name: searchText, type: listParams.type, location: listParams.location })
  }, [page])

  useEffect(() => {
    if (!isIntegrationScanner(scannerSelect) && caseListFor3Shape) {
      setFilterList([...caseListFor3Shape])
    }
  }, [caseListFor3Shape, scannerSelect])

  // Device scanners: list from existing getCaseList / thunk pipeline
  useDeepEffect(() => {
    if (showScannerPatients && isDeviceScanner(listParams.type)) {
      dispatch(
        get3ShapeCaseList({
          page: listParams.page,
          type: listParams.type as "3Shape" | "Medit" | "Alliedstar",
          name: listParams.name,
        }),
      )
    }
  }, [listParams.type, listParams.name, listParams.page, showScannerPatients, dispatch])

  const windowHeight = usePageHeight()

  const handleCaseDetail = (row: Record<string, any>) => {
    if (scannerSelect === "3Shape") {
      // stl and image
      const { Scans = [], Attachments = [], Info } = row
      const obj: Record<string, any> = {}
      Scans.map((i) => {
        if (i.FileType === "stl" && i.Href) {
          if (i.JawType === "upper") {
            const pre = i.Href.split("attachments")[0]
            obj.upper = `${pre}attachments/${i.Hash}`
          }
          if (i.JawType === "lower") {
            const pre = i.Href.split("attachments")[0]
            obj.lower = `${pre}attachments/${i.Hash}`
          }
        }
      })
      const imageTmp = []
      let birthHref = ""
      Attachments.map((i) => {
        if (["png", "jpg", "jpeg"].includes(i.FileType) && i.Href) {
          const imageObj: Record<string, string> = {}
          const pre = i.Href.split("attachments")[0]
          imageObj.href = `${pre}attachments/${i.Hash}`
          imageObj.name = i.Name
          imageObj.fileType = `image/${i.FileType}`
          imageTmp.push(imageObj)
        }
        if (i.FileType === "html") {
          birthHref = i.Href
        }
      })
      let InfoObj: Record<string, any> =
        Info == null ? {} : structuredClone(Info)
      obj.Info = InfoObj
      obj.Scans = Scans
      obj.Attachments = imageTmp
      let comment = ""
      if (row.Info?.Comments?.length > 0) {
        row.Info.Comments.map((i) => {
          // getCaseComment(row.Info.CaseId, i.id).then((res) => {
          //   if (res) {
          //     // setValue("notes", res)
          //     comment += res + "\n"
          //   }
          // })
          comment += i.ShortComment + "\n"
        })
      }
      obj.Info.notes = comment
      obj.Info.year = ""
      obj.Info.month = ""
      obj.Info.day = ""
      // set birthday
      if (birthHref !== "") {
        console.log("3shapebirth_param_ui", birthHref)
        try {
          dispatch(getBirthdayfor3Shape({ url: birthHref })).then((res) => {
            console.log("Birthday res", res)
            if (res) {
              const { year, month, day } = res.payload as {
                year?: string
                month?: string
                day?: string
              }
              obj.Info.year = year || ""
              obj.Info.month = month || ""
              obj.Info.day = day || ""
            }
            dispatch(setData({ caseDetailForShape: obj }))
            choosePatient && choosePatient(obj)
          })
        } catch (error) {
          console.error(error)
          obj.Info.year = ""
          obj.Info.month = ""
          obj.Info.day = ""
          dispatch(setData({ caseDetailForShape: obj }))
          choosePatient && choosePatient(obj)
        }
      } else {
        dispatch(setData({ caseDetailForShape: obj }))
        choosePatient && choosePatient(obj)
      }
    }
    if (scannerSelect === "Alliedstar") {
      const detail = JSON.parse(JSON.stringify(row))
      getCaseDetail({ type: "Alliedstar", id: row.Info.CaseId }).then((res) => {
        if (res && res.attachments) {
          const scanTmp = []
          const imageTmp = []
          res.attachments.map((i) => {
            if (i.fileFormat === "stl") {
              scanTmp.push(i)
            } else {
              imageTmp.push(i)
            }
          })
          detail.Attachments = imageTmp
          detail.Scans = scanTmp
          detail.Info.notes = res.comment
          detail.Info.year = ""
          detail.Info.month = ""
          detail.Info.day = ""
          // setValue("notes", res.comment)
          getPatientslist(res.patientName, res.patientId).then((res1) => {
            const resData = res1 as {
              data?: { data?: { birthDate?: string }[] }
            }
            if (res1 && resData.data) {
              if (resData.data.data?.[0]?.birthDate) {
                const dob = resData.data.data[0].birthDate
                  .split("T")[0]
                  .split("-")
                detail.Info.month = dob[1] || ""
                detail.Info.day = dob[2] || ""
                detail.Info.year = dob[0] || ""
              }
            }
            choosePatient && choosePatient(detail)
            dispatch(setData({ caseDetailForShape: detail }))
          })
        }
      })
    }
    if (scannerSelect === "Medit") {
      const detail = JSON.parse(JSON.stringify(row))
      getCaseByUuid(row.Info.CaseId).then((res) => {
        if (res && res.data.files) {
          const scanTmp = []
          const imageTmp = []
          const keywords = ["mandibular", "maxillary"]
          const imageKeywords = ["jpg", "png", "jpeg"]
          res.data.files.map((i) => {
            if (keywords.some((keyword) => i.name.includes(keyword))) {
              scanTmp.push(i)
            } else if (
              imageKeywords.some((keyword) =>
                i.name.toLowerCase().includes(keyword),
              )
            ) {
              imageTmp.push(i)
            }
          })
          detail.Attachments = imageTmp
          detail.Scans = scanTmp
        }
        dispatch(setData({ caseDetailForShape: detail }))
      })
      choosePatient && choosePatient(detail)
    }
  }

  // 清理meditData并处理其余scanner
  const handleMeditData = () => {
    const shapeScanner = JSON.parse(localStorage.getItem("3shapeData") || "{}")
    const meditScanner = JSON.parse(localStorage.getItem("meditData") || "{}")
    const deleteStorageObj = {
      name: scannerSelect,
      key: scannerSelect,
      isPrimary:
        scannerSelect === "Medit"
          ? meditScanner.isPrimary
          : shapeScanner.isPrimary,
      localName: scannerSelect === "Medit" ? "meditData" : "3shapeData",
    }
    let preparatoryPrimary = ""
    if (deleteStorageObj.isPrimary && scannerSelectData.length > 1) {
      scannerSelectData.forEach((item) => {
        if (item.name !== deleteStorageObj.name && item.localName) {
          preparatoryPrimary = item.localName
        }
      })
    }
    localStorage.removeItem(deleteStorageObj.localName)
    if (preparatoryPrimary !== "") {
      const userStr = localStorage.getItem(preparatoryPrimary)
      if (userStr) {
        const userObj = JSON.parse(userStr)
        userObj.isPrimary = true
        localStorage.setItem(preparatoryPrimary, JSON.stringify(userObj))
      }
    }
    const url =
      scannerSelect === "Medit"
        ? `https://openapi-auth.meditlink.com/oauth/authorize?client_id=A05M4Esrwv8WBW1ybGqSnJ3OQbsjW0q0&response_type=code&redirect_uri=${VITE_DOMAIN}/settings/integrations?from=medit&scope=USER%20GROUP%20CASE%20FILE%20ORDER%20PRODUCT:DENTAL%20FILE:MANAGE%20ORDER:MANAGE&state=bn6q6ru91ygkzwo8rfc0s1f7`
        : `https://identity.3shape.com/connect/authorize?client_id=UlUTXMHM7GGRTVb&response_type=code&scope=openid+api+offline_access+communicate.connections.manage+data.companies.read_only+data.users.read_only&redirect_uri=${VITE_DOMAIN}/settings/integrations&code_challenge=mwofHihL4F89LWgqhai-9GTAAhJtTIvR5bOqz_3_B9E&code_challenge_method=S256&response_mode=query`
    window.open(url)
  }

  const isSelectedIntegrationScanner = isIntegrationScanner(scannerSelect)

  const showLocationFilter =
    isSelectedIntegrationScanner &&
    patientListDetails.location &&
    patientListDetails.location.length > 0

  const dialogScale = windowHeight / 945 > 1 ? 1 : windowHeight / 945
  const scaledDialogWidth = (showLocationFilter ? 650 : 564) * dialogScale

  const listTotalCount = isSelectedIntegrationScanner
    ? patientListDetails.filtered_count
    : scannerCaseListCount

  return (
    <>
      <Dialog
        sx={{
          "& .MuiDialog-container": {
            height: "100%",
          },
          "& .MuiPaper-root": {
            borderRadius: "16px",
          },
          "& .MuiDialog-paper": {
            maxHeight: "92%",
            overflow: "hidden",
            width: scaledDialogWidth,
            maxWidth: scaledDialogWidth,
          },
        }}
        open={showScannerPatients}
      >
        <Box
          sx={{
            width: showLocationFilter ? 650 : 564,
            height: 880,
            minHeight: 880,
            padding: "32px",
            display: "flex",
            flexDirection: "column",
            transform: `scale(${dialogScale})`,
            transformOrigin: "top left",
          }}
        >
          <Box sx={{ height: "96px", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
              <Select
                value={scannerSelect}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "32px",
                  },
                  "& .MuiSelect-select": {
                    padding: "0 32px 0 0",
                    fontSize: "24px",
                    color: "rgba(0,0,0,0.87)",
                    minHeight: "unset !important",
                  },
                  "& .MuiSvgIcon-root": {
                    right: "0",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    display: "none",
                    border: "none", // 去除默认边框
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    border: "none", // 去除悬停状态边框
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "none", // 去除聚焦状态边框
                  },
                }}
                IconComponent={ExpandMore} // 替换为 Material-UI 内置图标
                // MenuProps={{
                //   PaperProps: {
                //     sx: {
                //       "& .MuiMenuItem-root": { padding: "8px 16px" },
                //     },
                //   },
                // }}
                onChange={async (event) => {
                  const value = event.target.value as string
                  setPage(0)
                  setSearchText("")

                  try {
                    if (isIntegrationScanner(value)) {
                      const allowed = await checkIntegrationConnection(value)
                      if (!allowed) {
                        setNotConnectedDialogScanner(value)
                        return
                      }
                    }

                    setListParams({
                      page: 0,
                      type: value,
                      name: "",
                      location: null, 
                    })
                    dispatch(setData({ scannerSelect: value }))
                    if (isIntegrationScanner(value)) {
                      clearIntegrationTableRows()
                    }
                  } catch {
                    // Revert the Select to the previous value so UI stays in sync with Redux state
                    dispatch(setData({ scannerSelect: scannerSelect }))
                  }
                }}
              >
                {scannerSelectData.map((i) => {
                  return (
                    <MenuItem key={i.key} value={i.key}>
                      {i.name}
                    </MenuItem>
                  )
                })}
              </Select>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {isSelectedIntegrationScanner && syncNow !== "SYNCING" && (
                  <UText variant="caption">
                    Last synced {lastSyncedRelative}
                  </UText>
                )}
                {isSelectedIntegrationScanner && syncNow === "SYNCING" ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      pr: 0.5,
                      pt: 1,
                      pb: 2,
                    }}
                  >
                    <CachedIcon
                      sx={{
                        fontSize: 18,
                        "@keyframes scanner-sync-spin": {
                          from: { transform: "rotate(0deg)" },
                          to: { transform: "rotate(360deg)" },
                        },
                        animation: "scanner-sync-spin 1s linear infinite",
                      }}
                    />
                    <UText variant="caption">Syncing</UText>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      overflow: "hidden",
                      width:
                        isSelectedIntegrationScanner && syncNow === "SHOWN"
                          ? "82px"
                          : "0px",
                      transition: "width 0.3s ease-in-out",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    {isSelectedIntegrationScanner && syncNow === "SHOWN" && (
                      <UButton
                        btnText={"Sync Now"}
                        variant="text"
                        size="small"
                        sxProp={{
                          ...theme.typography.caption,
                          textTransform: "none",
                          whiteSpace: "nowrap",
                          minWidth: "auto",
                          borderRadius: "30px",
                          height: "30px",
                          px: "10px",
                        }}
                        onClickHandler={syncNowClickHandler}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{ width: "100%", margin: "16px 0", display: "flex", flexDirection: "row", gap:1}}>
              <Box sx={{ flexGrow: 1 }}>
                <UTableSearch
                  onSearch={debouncedSetSearchText}
                  searchText={searchText}
                />
              </Box>
              {showLocationFilter && (
                <Select sx={{ height: "40px", width: "170px" }} value={listParams.location} onChange={(eve) => {
                  setListParams({ ...listParams, location: eve.target.value })
                }}>
                  <MenuItem value={null}>
                    <em>None</em>
                  </MenuItem>
                  {patientListDetails.location.map((location) => (
                    <MenuItem key={location} value={location}>{location}</MenuItem>
                  ))}
                </Select>
              )}
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflow: "hidden", pt: 2 }}>
            <TableContainer
              component={Paper}
              sx={{
                border: "0",
                borderRadius: "4px !important",
                boxShadow: "none",
                background: "rgba(0,0,0,0.12)",
                borderColor: "rgba(0,0,0,0.23)",
                marginTop: "3px",
                padding: "5px 10px",
                "& .MuiTable-root": {
                  borderSpacing: "0 7px", // 纵向间隙高度
                  borderCollapse: "separate", // 必须设置
                },
              }}
            >
              <Table aria-label="scanner patients" size="small">
                <TableBody>
                  {/* .slice(page * rowsPerPage, (page + 1) * rowsPerPage) */}
                  {filterList &&
                    filterList.map((row) => (
                      <Tooltip
                        key={String(row.Info.CaseId)}
                        title={HIGHLIGHT_CURRENT_PRESCRIPTION && prescriptionId && String(row.Info.CaseId) === String(prescriptionId) ? "Currently selected" : ""}
                        placement="left"
                        arrow
                      >
                      <TableRow
                        sx={{
                          background: "#fff",
                          height: "48px",
                          padding: "17px",
                          borderRadius: "4px",
                          overflow: "hidden",
                          cursor: "pointer",
                          "&:first-of-type": {
                            borderTopLeftRadius: "4px",
                            borderTopRightRadius: "4px",
                          },
                          "&:last-of-type": {
                            borderBottomLeftRadius: "4px",
                            borderBottomRightRadius: "4px",
                          },
                          "&:hover": {
                            backgroundColor: "#f5f5f5", // 自定义颜色‌:ml-citation{ref="3,4" data="citationList"}
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)", // 添加投影效果‌:ml-citation{ref="3" data="citationList"}
                          },
                        }}
                        onClick={() => {
                          setShowScannerPatients(false)
                          if (isIntegrationScanner(scannerSelect)) {
                            handleScannerPatientDetailSelect?.(row.Info)
                          } else {
                            handleCaseDetail(row)
                          }
                          if (isIntegrationScanner(scannerSelect)) {
                            dispatch(setSelectedScannerPatient({
                              external_case_id: row.Info.CaseId,
                              scanner: scannerSelect as ScannerIntegrationId,
                            }))
                          }
                        }}
                      >
                        <TableCell
                          sx={{
                            border: "none",
                            "&:first-of-type": {
                              borderLeft: HIGHLIGHT_CURRENT_PRESCRIPTION && prescriptionId && String(row.Info.CaseId) === String(prescriptionId)
                                ? `6px solid ${theme.palette.primary.main}`
                                : "6px solid transparent",
                              borderTopLeftRadius: "inherit",
                              borderBottomLeftRadius: "inherit",
                            },
                            "&:last-of-type": {
                              borderRight: "1px solid #e0e0e0",
                              borderTopRightRadius: "inherit",
                              borderBottomRightRadius: "inherit",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              height: "40px",
                              display: "flex",
                              alignItems: "center",
                              fontSize: "16px",
                            }}
                          >
                            <PersonIcon
                              sx={{
                                fontSize: "40px",
                                color: "#9E9E9E",
                                marginRight: "16px",
                              }}
                            />
                            <span
                              style={{
                                width: isSelectedIntegrationScanner && row.Info.location ? "200px" : "unset",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.Info.PatientName}
                            </span>
                          </Box>
                        </TableCell>
                        {isSelectedIntegrationScanner && row.Info.location && (
                          <TableCell>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {row.Info.location}
                            </span>
                          </TableCell>
                        )}
                        <TableCell
                          sx={{
                            border: "none",
                            color: "rgba(0,0,0,0.6)",
                            textAlign: "right",
                            "&:first-of-type": {
                              borderLeft: "1px solid #e0e0e0",
                              borderTopLeftRadius: "inherit", // 继承行级圆角
                              borderBottomLeftRadius: "inherit",
                            },
                            "&:last-of-type": {
                              borderRight: "1px solid #e0e0e0",
                              borderTopRightRadius: "inherit",
                              borderBottomRightRadius: "inherit",
                            },
                          }}
                        >
                          {formatUTC(row.Info.CreateTime)}
                        </TableCell>
                      </TableRow>
                      </Tooltip>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              paddingRight: 2,
            }}
          >
            <Pagination
              count={listTotalCount === 0 ? 0 : Math.ceil(listTotalCount / rowsPerPage)}
              page={page + 1}
              onChange={(e, newPage) => setPage(newPage - 1)}
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 2.5,
                padding: "0 0 0 55px",
                "& .MuiPagination-ul": {
                  flexWrap: "nowrap",
                },
                maxWidth: "348px",
              }}
              showFirstButton
              showLastButton
              siblingCount={0}
              boundaryCount={1}
            />
            <Typography
              variant="body2"
              sx={{
                mt: 2.5,
                whiteSpace: "nowrap",
                fontSize: "14px",
                color: "rgba(0,0,0,0.6)",
              }}
            >
              {listTotalCount === 0
                ? 0
                : page * rowsPerPage + 1}
              -
              {Math.min(page * rowsPerPage + rowsPerPage, listTotalCount)}{" "}
              of {listTotalCount}
            </Typography>
          </Box>
          <Box
            sx={{
              marginTop: "16px",
              height: "36px",
              width: "100%",
              display: "flex",
              justifyContent: "end",
            }}
          >
            <UButton
              variant="shade"
              btnText={"Close"}
              sxProp={{
                width: "78px",
                height: "36px",
                padding: "6px 16px",
                "&:hover": {
                  backgroundColor: "#c9c9c9",
                  boxShadow: "1px 1px 1px 1px rgba(0,0,0,0.1)",
                },
              }}
              onClickHandler={() => {
                setShowScannerPatients(false)
                // dispatch(setData({ caseListFor3Shape: [] }))
                // scannerSelectDataReady.current = false
              }}
            ></UButton>
          </Box>
        </Box>
      </Dialog>
      <IntegrationNotConnectedDialog
        open={notConnectedDialogScanner !== null}
        scannerName={notConnectedDialogScanner ?? ""}
        onClose={closeNotConnectedDialog}
        onOk={() => {
          const key = notConnectedDialogScanner
          closeNotConnectedDialog()
          if (key) {
            suppressScanner(key)
          }
        }}
      />
      <Dialog open={meditLoginPopUp}>
        {/* <DialogTitle id="alert-dialog-title">
          Scanner login token expired, need to sign-in again from scanner
            login page.
        </DialogTitle> */}
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            sx={{ color: "rgba(0,0,0,1)", fontSize: "18px" }}
          >
            Scanner login token expired, need to sign-in again from scanner
            login page.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <UButton
            variant="contained"
            btnText={"Re-login"}
            sxProp={{
              width: "auto",
              height: "36px",
              padding: "6px 16px",
              // background: "#e0e0e0",
              // color: "rgba(0,0,0,0.87)",
            }}
            onClickHandler={() => {
              // 关闭两个弹窗
              setShowScannerPatients(false)
              dispatch(setMeditLoginPopUp(false))
              // 清理本地medit 数据,并处理其余scanner
              handleMeditData()
            }}
          ></UButton>
        </DialogActions>
      </Dialog>
    </>
  )
}
export default ScannerPatients
